import { Component, computed, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { formatBreakTime } from '@app/+authenticated/+timesheet/shared/timesheet-break.helper';
import { ClockMapDialogComponent } from '@app/+modals/timesheet/timesheet-hours/clock-location/clock-map-dialog.component';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { isClockOrigin, originOptions } from '@reducers/auth/auth.service';
import { AppState } from '@reducers/interfaces';
import { ConflictType, TimesheetConflictStoreModel } from '@reducers/orm/timesheet-conflict/timesheet-conflict.model';
import { getTimesheetConflictsByTimesheetId } from '@reducers/orm/timesheet-conflict/timesheet-conflict.selectors';
import { TimesheetClockOriginLabels, TimesheetModelEnchanced } from '@reducers/orm/timesheet/timesheet.model';
import { TimesheetColumns } from '@reducers/page-filters/page-filters.model';
import { DialogWidth, SbDialogService } from '@sb/ui/dialog';
import { PlanType } from '@shiftbase-com/ts-shared/models';
import { Subscription } from 'rxjs';

/**
 * The timesheet as this component's template consumes it: the enhanced model
 * plus the display-only values the setter derives below. None of the extra
 * fields come from the API.
 */
interface TimesheetDetailsView extends TimesheetModelEnchanced, TimesheetClockOriginLabels {
  overtimeHour: number;
  overtimeMinutes: number;
}

@Component({
  standalone: false,
  selector: 'timesheet-details',
  templateUrl: './timesheet-details.component.html',
})
export class TimesheetDetailsComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);
  private store = inject<Store<AppState>>(Store);
  private dialog = inject(SbDialogService);

  private _timesheet: TimesheetDetailsView;

  planType = PlanType;

  @Input()
  absenceWarning: boolean;
  @Input()
  doubleRegistration: boolean;
  @Input()
  cols: TimesheetColumns;

  conflict: TimesheetConflictStoreModel;
  hasTimesheetConflict: boolean;
  hasAbsenceConflict: boolean;

  private dataSubs = new Subscription();

  hasRateCard = false;

  #clockedBreak = signal<string | undefined>(undefined);
  formattedBreakTime = computed(() => formatBreakTime(this.#clockedBreak()));

  @Input()
  set timesheet(timesheet: TimesheetModelEnchanced) {
    this.#clockedBreak.set(timesheet.clocked_break);
    this._timesheet = {
      ...timesheet,
      canViewClockTime: timesheet.canViewClockTime,
      ClockedInOrigin: this.translateClockOrigin(timesheet.clocked_in_origin),
      ClockedOutOrigin: this.translateClockOrigin(timesheet.clocked_out_origin),
      overtimeHour: parseInt(timesheet?.overtime?.split('.')[0]),
      overtimeMinutes: parseInt(this.formatOvertimeMinutes(parseInt(timesheet?.overtime?.split('.')[1]) * 0.6)),
    };

    this.hasRateCard = !!this.timesheet?.Rates?.RateBlock?.find(
      (rateBlock) =>
        !rateBlock?.is_overtime &&
        (parseFloat(rateBlock?.surcharge_pay.toString()) !== 0 ||
          parseFloat(rateBlock?.surcharge_time.toString()) !== 0),
    );
  }

  get timesheet(): TimesheetDetailsView {
    return this._timesheet;
  }

  ngOnInit() {
    this.dataSubs.add(
      this.store.select(getTimesheetConflictsByTimesheetId(this.timesheet.id)).subscribe({
        next: (timesheetConflict: TimesheetConflictStoreModel) => {
          this.hasTimesheetConflict = timesheetConflict?.conflicts?.some(
            (conflict) => conflict.type === ConflictType.TIMESHEET,
          );
          this.hasAbsenceConflict = timesheetConflict?.conflicts?.some(
            (conflict) => conflict.type === ConflictType.ABSENCE,
          );
        },
      }),
    );
  }

  ngOnDestroy() {
    this.dataSubs.unsubscribe();
  }

  showClockMap(): void {
    if (!this.timesheet.canViewClockTime) {
      return;
    }
    this.dialog.setSize('lg').open(ClockMapDialogComponent, { data: { timesheet: this.timesheet } });
  }

  private translateClockOrigin(origin: string): string {
    return isClockOrigin(origin) ? this.translate.instant(originOptions[origin]) : origin;
  }

  formatOvertimeMinutes(minutes: number): string {
    const formattedMinutes = Math.round(minutes);
    return formattedMinutes < 10 ? `0${formattedMinutes}` : `${formattedMinutes}`;
  }
}
